/**
 * Identity evidence — the opposite rule to the photograph, and worded so.
 *
 * PRIVATE-ONLY, FOR EVER. `claimCredentialUpload` stores the file only under the private prefix:
 * no rendition and no public object, ever, enforced by a database CHECK on the catalogue rather
 * than by code. `Credential` carries NO object URL on any surface — so this is a list of file
 * names and dates, never a thumbnail and never a download.
 *
 * NO WITHDRAWAL, AND THE ABSENCE IS DELIBERATE. The contract's own preamble says it: "there is no
 * author-initiated credential-withdrawal route (unfiling a credential is a maintenance act and
 * `moonx_app` is revoked from it)". So this panel offers no delete, and says why rather than
 * leaving somebody hunting for one.
 *
 * DESTROYED 90 DAYS AFTER THE DECISION, not after the upload — so an applicant who waits three
 * weeks for a review does not lose their evidence mid-review. `fileDestroyedAt` is how a row
 * reports that it has happened, and `fileName` goes null with the file, because the name goes with
 * the file.
 *
 * IT IS OPTIONAL. Five weight points, and it blocks nothing.
 *
 * ITS REFUSALS ARE WORDED AS EVIDENCE. A PDF is accepted here and not for the photograph, so a
 * refusal is asked for with the `'credential'` subject (`UploadSubject`, ../api/problem.ts) - the
 * photograph's sentence would tell somebody holding a PDF certificate that it cannot be sent.
 *
 * An approved author is not told it "helps a reviewer place you" (the owner's live walk of real
 * cabinets, 2026-10-01): they have no reviewer. `applying` false drops that clause - and the
 * promise that the file "is destroyed ninety days after your application is decided", which is
 * not true for them: the retention pass destroys a file 90 days after the date the VERDICT stamps
 * on it (`destroyDueCredentials`, src/tools/retention-pass/retention-pass.ts), and a file filed
 * after the decision - or by an author the import brought over, who never applied - gets no stamp
 * and is never due. Reported to the owner as a question, not settled here.
 */
import { useRef } from 'react';
import { Badge, Button, Group, Paper, Stack, Text } from '@mantine/core';
import type { ReactNode } from 'react';
import { Trans, useTranslation } from '../../../i18n';
import type { Credential } from '../contract';
import { CREDENTIAL_ACCEPT } from '../domain/limits';
import { humanMessage } from '../api/problem';
import type { CredentialUpload } from '../data/useUpload';
import { EmptyPanel, InlineFailure, LoadingState } from './States';

export interface CredentialsPanelProps {
  readonly id: string;
  readonly items: readonly Credential[] | undefined;
  readonly loading: boolean;
  readonly loadError: unknown;
  readonly onRetryList: () => void;
  readonly upload: CredentialUpload;
  readonly disabled?: boolean;
  /** Whether this is still an application (`rowsGateSending`). Omitted, it is. */
  readonly applying?: boolean;
}

/** ISO instant to its UTC day, as the cabinet writes one (design 24, T16): never the browser's. */
function formatDate(value: string): string {
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return value;
  return formatDay(parsed.toISOString().slice(0, 10));
}

export function CredentialsPanel({
  id,
  items,
  loading,
  loadError,
  onRetryList,
  upload,
  disabled,
  applying = true,
}: CredentialsPanelProps): ReactNode {
  const { t } = useTranslation('application');
  const inputRef = useRef<HTMLInputElement>(null);
  const busy = upload.phase === 'writing' || upload.phase === 'claiming';

  const picker = (
    <Stack gap="xs" align="flex-start">
      <input
        ref={inputRef}
        type="file"
        accept={CREDENTIAL_ACCEPT.join(',')}
        hidden
        onChange={(event) => {
          const file = event.currentTarget.files?.[0];
          if (file !== undefined) void upload.upload(file);
          event.currentTarget.value = '';
        }}
      />
      <Button
        size="xs"
        variant="light"
        loading={busy}
        disabled={disabled === true}
        onClick={() => inputRef.current?.click()}
      >
        {t('credentials.add')}
      </Button>
      {upload.phase === 'writing' ? (
        <Text size="xs" c="dimmed" aria-live="polite">
          {t('upload.uploading', { percent: Math.round(upload.progress * 100) })}
        </Text>
      ) : null}
      {upload.phase === 'claiming' ? (
        <Text size="xs" c="dimmed" aria-live="polite">
          {t('credentials.filing')}
        </Text>
      ) : null}
      {upload.phase === 'failed' ? (
        <Group gap="xs" role="alert">
          <Text size="xs" style={{ color: 'oklch(var(--mx-danger))' }}>
            {humanMessage(upload.error, t('credentials.failed'), 'credential', t)}
          </Text>
          <Button size="compact-xs" variant="subtle" onClick={upload.reset}>
            {t('upload.tryAnother')}
          </Button>
        </Group>
      ) : null}
    </Stack>
  );

  return (
    <Stack gap="sm" id={id}>
      <Stack gap={2}>
        <Text fw={700} size="sm">
          {t('credentials.title')}
        </Text>
        <Text size="xs" c="dimmed">
          <Trans
            t={t}
            i18nKey={applying ? 'credentials.intro.applying' : 'credentials.intro.author'}
            components={{ strong: <strong /> }}
          />
        </Text>
      </Stack>

      {loading ? (
        <LoadingState label={t('credentials.loading')} lines={2} />
      ) : loadError !== undefined && loadError !== null ? (
        <InlineFailure
          error={loadError}
          fallback={t('credentials.loadFailed')}
          onRetry={onRetryList}
        />
      ) : items === undefined || items.length === 0 ? (
        <EmptyPanel
          title={t('credentials.empty.title')}
          description={applying ? t('credentials.empty.applying') : t('credentials.empty.author')}
          action={picker}
        />
      ) : (
        <Stack gap="xs">
          <Paper withBorder radius="md" p="sm">
            <Stack gap="xs" component="ul" style={{ listStyle: 'none', margin: 0, padding: 0 }}>
              {items.map((item, index) => {
                const destroyed =
                  item.fileDestroyedAt !== null && item.fileDestroyedAt !== undefined;
                return (
                  <Group
                    component="li"
                    key={`${item.filedAt}-${item.fileName ?? 'destroyed'}-${index}`}
                    justify="space-between"
                    wrap="nowrap"
                    gap="sm"
                  >
                    <Stack gap={0} style={{ minWidth: 0 }}>
                      <Text size="sm" truncate>
                        {/* The name goes with the file: once destroyed, `fileName` is null. */}
                        {item.fileName ?? t('credentials.fileDestroyed')}
                      </Text>
                      <Text size="xs" c="dimmed">
                        {t('credentials.filed', { date: formatDate(item.filedAt) })}
                        {item.applicationRound !== null && item.applicationRound !== undefined
                          ? ` · ${t('credentials.round', { round: item.applicationRound })}`
                          : ''}
                        {destroyed && item.fileDestroyedAt
                          ? ` · ${t('credentials.destroyedOn', { date: formatDate(item.fileDestroyedAt) })}`
                          : ''}
                      </Text>
                    </Stack>
                    <Badge size="xs" variant="default" style={{ flex: 'none' }}>
                      {destroyed ? t('credentials.badge.destroyed') : t('credentials.badge.held')}
                    </Badge>
                  </Group>
                );
              })}
            </Stack>
          </Paper>
          {picker}
        </Stack>
      )}
    </Stack>
  );
}

// The cabinet's day formatter (design 24, T16), imported at the foot so no line above moves.
import { formatDay } from '../../cabinet/format';
